/*
  TrainingOverview — manager-only "team learning health" dashboard.

  Rolls up every active staff member against three dimensions:
    1. Training assignments (assigned / in progress / completed / overdue)
    2. Qualification expiry (upcoming + already-expired certs)
    3. Announcement acknowledgement debt (who hasn't ticked the box)

  Rendered as a toggleable panel at the top of the Learning → Training
  tab for owners and managers. Staff users never see it.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, Loader2, Search, RefreshCcw, AlertTriangle, CheckCircle2,
  Clock, Award, PlaySquare, Megaphone, ArrowRight, AlertCircle, Send,
} from "lucide-react";

function Tile({ icon: Icon, label, value, tone = "zinc", testId, onClick }) {
  const tones = {
    zinc: "border-zinc-200 bg-white text-zinc-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    red: "border-red-200 bg-red-50 text-red-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-900",
  };
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={`text-left border rounded-md px-3 py-2.5 transition-shadow ${tones[tone]} ${
        onClick ? "hover:shadow-sm cursor-pointer" : "cursor-default"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-2xs uppercase tracking-wider font-medium opacity-80">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-2xl font-semibold mt-0.5 leading-none">{value ?? 0}</div>
    </button>
  );
}

function statusChip(cell) {
  if (!cell) return <span className="text-zinc-300 text-2xs">—</span>;
  if (cell.competent_lapsed) {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs px-1.5 py-0 h-5">
        <AlertTriangle className="h-3 w-3 mr-0.5" />Lapsed
      </Badge>
    );
  }
  if (cell.competent) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-2xs px-1.5 py-0 h-5">
        <Award className="h-3 w-3 mr-0.5" />Competent
      </Badge>
    );
  }
  if (cell.status === "completed") {
    return (
      <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-2xs px-1.5 py-0 h-5">
        <CheckCircle2 className="h-3 w-3 mr-0.5" />Completed
      </Badge>
    );
  }
  if (cell.overdue) {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs px-1.5 py-0 h-5">
        <AlertTriangle className="h-3 w-3 mr-0.5" />Overdue
      </Badge>
    );
  }
  if (cell.status === "in_progress") {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-2xs px-1.5 py-0 h-5">
        <Clock className="h-3 w-3 mr-0.5" />In progress
      </Badge>
    );
  }
  if (cell.status === "assigned") {
    return (
      <Badge className="bg-zinc-100 text-zinc-700 border-zinc-200 text-2xs px-1.5 py-0 h-5">
        Assigned
      </Badge>
    );
  }
  return <span className="text-zinc-300 text-2xs">—</span>;
}

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function OverdueAssignmentDrawer({ open, onClose, data, onlyOverdue, onlyCompleted }) {
  const rows = useMemo(() => {
    if (!data) return [];
    const out = [];
    data.matrix.forEach((row) => {
      Object.entries(row.cells).forEach(([mid, cell]) => {
        if (onlyOverdue && !cell.overdue) return;
        if (onlyCompleted && cell.status !== "completed") return;
        if (!onlyOverdue && !onlyCompleted && (!cell.status || cell.status === "none")) return;
        const mod = data.modules.find((m) => m.module_id === mid);
        out.push({ user: row, module: mod, cell });
      });
    });
    out.sort((a, b) => (a.user.name || "").localeCompare(b.user.name || ""));
    return out;
  }, [data, onlyOverdue, onlyCompleted]);
  const title = onlyOverdue ? "Overdue assignments" : onlyCompleted ? "Completed in last 30 days" : "Assignments";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle data-testid="training-overview-drawer-title">{title}</DialogTitle>
          <DialogDescription>
            {rows.length} record{rows.length === 1 ? "" : "s"}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-zinc-100">
          {rows.length === 0 && (
            <div className="text-xs text-zinc-500 py-6 text-center">Nothing here — nice work!</div>
          )}
          {rows.map((r, i) => (
            <div key={`${r.user.user_id}-${r.module?.module_id}-${i}`} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate">{r.user.name}</div>
                <div className="text-xs text-zinc-500 truncate">{r.module?.title || "(module)"}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {statusChip(r.cell)}
                {r.cell.due_at && (
                  <span className="text-2xs text-zinc-500">
                    due {new Date(r.cell.due_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TrainingOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [drawerKind, setDrawerKind] = useState(null); // "overdue" | "completed" | null
  const [nudging, setNudging] = useState(false);
  const [nudgingAnnId, setNudgingAnnId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/training/overview");
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load team overview", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function nudgeAllOverdue() {
    if (!window.confirm("Send a reminder (in-app + email) to every staff member with overdue training?")) return;
    setNudging(true);
    try {
      const r = await api.post("/training/overview/nudge-overdue");
      toast.success(
        r.data.notified_users === 0
          ? "No overdue assignments right now."
          : `Nudged ${r.data.notified_users} staff member${r.data.notified_users === 1 ? "" : "s"}`,
        { description: `${r.data.emails_sent} email${r.data.emails_sent === 1 ? "" : "s"} sent · ${r.data.assignments} overdue assignment${r.data.assignments === 1 ? "" : "s"}` }
      );
    } catch (e) {
      toast.error("Couldn't send nudges", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setNudging(false); }
  }

  async function nudgePendingAck(ann) {
    if (!window.confirm(`Re-send the acknowledgement prompt to the ${ann.pending} pending staff member${ann.pending === 1 ? "" : "s"} for "${ann.title}"?`)) return;
    setNudgingAnnId(ann.announcement_id);
    try {
      const r = await api.post(`/announcements/${ann.announcement_id}/nudge-pending`);
      toast.success(
        r.data.notified_users === 0
          ? "Everyone has already acknowledged — nothing to send."
          : `Reminder sent to ${r.data.notified_users} staff member${r.data.notified_users === 1 ? "" : "s"}`,
        { description: `${r.data.emails_sent} email${r.data.emails_sent === 1 ? "" : "s"} sent` }
      );
    } catch (e) {
      toast.error("Couldn't send ack reminder", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setNudgingAnnId(null); }
  }

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.matrix;
    return data.matrix.filter((r) =>
      (r.name || "").toLowerCase().includes(q)
      || (r.role || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500 py-6">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading team overview…
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary;
  return (
    <div className="space-y-3" data-testid="training-overview-panel">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <Tile
          icon={Users} label="Staff" value={s.total_staff}
          testId="overview-tile-staff"
        />
        <Tile
          icon={Clock} label="In progress" value={s.assignments_in_progress}
          tone="amber" testId="overview-tile-in-progress"
        />
        <Tile
          icon={AlertTriangle} label="Overdue" value={s.assignments_overdue}
          tone={s.assignments_overdue > 0 ? "red" : "zinc"}
          testId="overview-tile-overdue"
          onClick={s.assignments_overdue > 0 ? () => setDrawerKind("overdue") : undefined}
        />
        <Tile
          icon={CheckCircle2} label="Completed 30d" value={s.assignments_completed_30d}
          tone="emerald" testId="overview-tile-completed-30d"
          onClick={s.assignments_completed_30d > 0 ? () => setDrawerKind("completed") : undefined}
        />
        <Tile
          icon={Award} label="Certs expiring" value={s.qualifications_expiring_soon + s.qualifications_expired}
          tone={s.qualifications_expired > 0 ? "red" : s.qualifications_expiring_soon > 0 ? "amber" : "zinc"}
          testId="overview-tile-qualifications"
        />
        <Tile
          icon={Megaphone} label="Ack debt" value={s.ack_pending_total}
          tone={s.ack_pending_total > 0 ? "indigo" : "zinc"}
          testId="overview-tile-ack-debt"
        />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-[360px]">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff by name or role"
            className="h-8 text-xs pl-6"
            data-testid="overview-staff-search"
          />
        </div>
        {s.assignments_overdue > 0 && (
          <Button
            variant="outline"
            onClick={nudgeAllOverdue}
            disabled={nudging}
            className="text-xs h-8 border-red-300 text-red-800 hover:bg-red-50"
            data-testid="overview-nudge-overdue-btn"
            title="Fires an in-app notification + email to every staff member with at least one overdue training assignment."
          >
            {nudging
              ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              : <Send className="h-3 w-3 mr-1" />}
            Nudge all overdue
          </Button>
        )}
        <Button variant="outline" onClick={load} className="text-xs h-8" data-testid="overview-refresh-btn">
          <RefreshCcw className="h-3 w-3 mr-1" /> Refresh
        </Button>
        <div className="text-2xs text-zinc-400 ml-auto">
          Updated {formatRelative(data.generated_at)}
        </div>
      </div>

      {/* Matrix — users × modules */}
      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="text-xs font-medium text-zinc-700 px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-1.5">
          <PlaySquare className="h-3.5 w-3.5" /> Training matrix
          <span className="text-2xs text-zinc-500 font-normal">({data.modules.length} modules × {filteredRows.length} staff)</span>
        </div>
        {data.modules.length === 0 ? (
          <div className="p-6 text-xs text-zinc-500 text-center">
            No training modules yet. Create your first module from the Training tab to start tracking.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="overview-matrix-table">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
                  <th className="px-3 py-2 font-medium text-zinc-700 sticky left-0 bg-zinc-50 z-10 min-w-[180px]">Staff</th>
                  {data.modules.map((m) => (
                    <th key={m.module_id} className="px-2 py-2 font-medium text-zinc-700 min-w-[120px]">
                      <Link to={`/learning/training/${m.module_id}`} className="hover:text-indigo-700 line-clamp-2" title={m.title}>
                        {m.title}
                      </Link>
                      <div className="text-2xs text-zinc-400 font-normal mt-0.5">
                        {m.completed}/{m.total_assigned} done
                        {m.overdue > 0 && <span className="text-red-600 ml-1">· {m.overdue} od</span>}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.user_id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                    <td className="px-3 py-2 sticky left-0 bg-white z-10" data-testid={`overview-row-${row.user_id}`}>
                      <div className="font-medium text-zinc-900 truncate">{row.name}</div>
                      <div className="text-2xs text-zinc-500 capitalize">{row.role}</div>
                    </td>
                    {data.modules.map((m) => (
                      <td key={m.module_id} className="px-2 py-2 align-top" data-testid={`overview-cell-${row.user_id}-${m.module_id}`}>
                        {statusChip(row.cells[m.module_id])}
                        {row.cells[m.module_id]?.competent_renew_by && (
                          <div
                            className={`text-2xs mt-0.5 ${row.cells[m.module_id]?.competent_lapsed ? "text-red-600 font-medium" : "text-zinc-400"}`}
                            title={row.cells[m.module_id]?.competent_lapsed ? "Competency renewal overdue" : "Renewal due"}
                          >
                            {row.cells[m.module_id]?.competent_lapsed ? "renew now" : "renew by"} {new Date(row.cells[m.module_id].competent_renew_by).toLocaleDateString()}
                          </div>
                        )}
                        {row.cells[m.module_id]?.due_at
                          && !row.cells[m.module_id]?.completed_at
                          && row.cells[m.module_id]?.status !== "completed"
                          && (
                          <div className="text-2xs text-zinc-400 mt-0.5">
                            due {new Date(row.cells[m.module_id].due_at).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-center text-xs text-zinc-500" colSpan={data.modules.length + 1}>
                      No staff match that search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Expiring qualifications */}
      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="text-xs font-medium text-zinc-700 px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-1.5">
          <Award className="h-3.5 w-3.5" /> Qualifications expiring soon
          <span className="text-2xs text-zinc-500 font-normal">(next 60 days + already expired)</span>
        </div>
        {data.expiring_qualifications.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500 text-center">
            No qualifications expiring in the next 60 days. ✓
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {data.expiring_qualifications.map((q) => (
              <div key={q.qualification_id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`overview-expiring-${q.qualification_id}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-zinc-900 truncate">
                    {q.user_name} — {q.name}
                  </div>
                  {q.issuer && (
                    <div className="text-2xs text-zinc-500 truncate">Issuer: {q.issuer}</div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  {q.expired ? (
                    <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs">
                      Expired {Math.abs(q.days_until_expiry)}d ago
                    </Badge>
                  ) : q.days_until_expiry <= 14 ? (
                    <Badge className="bg-red-100 text-red-800 border-red-200 text-2xs">
                      Expires in {q.days_until_expiry}d
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-2xs">
                      Expires in {q.days_until_expiry}d
                    </Badge>
                  )}
                  <div className="text-2xs text-zinc-400 mt-0.5">
                    {q.expiry_date ? new Date(q.expiry_date).toLocaleDateString() : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Announcement acknowledgement debt */}
      <Card className="rounded-lg border border-zinc-200 shadow-none overflow-hidden">
        <div className="text-xs font-medium text-zinc-700 px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-1.5">
          <Megaphone className="h-3.5 w-3.5" /> Announcements awaiting acknowledgement
        </div>
        {data.announcements_pending_ack.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500 text-center">
            Every ack-required announcement has been fully acknowledged. ✓
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {data.announcements_pending_ack.map((a) => (
              <div key={a.announcement_id} className="px-3 py-2" data-testid={`overview-ack-${a.announcement_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-zinc-900 truncate">{a.title}</div>
                    <div className="text-2xs text-zinc-500">
                      {a.acknowledged}/{a.audience_size} acknowledged
                      {a.published_at && <span className="ml-1">· published {formatRelative(a.published_at)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {a.pending > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-2xs px-2 border-indigo-300 text-indigo-800 hover:bg-indigo-50"
                        onClick={() => nudgePendingAck(a)}
                        disabled={nudgingAnnId === a.announcement_id}
                        data-testid={`overview-ack-nudge-${a.announcement_id}`}
                        title={`Re-send the acknowledgement prompt to the ${a.pending} pending staff member${a.pending === 1 ? "" : "s"}.`}
                      >
                        {nudgingAnnId === a.announcement_id
                          ? <Loader2 className="h-2.5 w-2.5 mr-1 animate-spin" />
                          : <Send className="h-2.5 w-2.5 mr-1" />}
                        Request ack
                      </Button>
                    )}
                    <Badge
                      className={`text-2xs ${
                        a.pending === 0
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : "bg-indigo-100 text-indigo-800 border-indigo-200"
                      }`}
                    >
                      {a.pending === 0 ? "All acknowledged" : `${a.pending} pending`}
                    </Badge>
                  </div>
                </div>
                {a.pending_users && a.pending_users.length > 0 && (
                  <details className="mt-1.5">
                    <summary className="cursor-pointer text-2xs text-zinc-500 hover:text-indigo-700 inline-flex items-center gap-0.5">
                      <ArrowRight className="h-2.5 w-2.5" /> Show pending staff
                    </summary>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {a.pending_users.map((u) => (
                        <span key={u.user_id} className="text-2xs px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded">
                          {u.name}
                        </span>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <OverdueAssignmentDrawer
        open={drawerKind === "overdue" || drawerKind === "completed"}
        onlyOverdue={drawerKind === "overdue"}
        onlyCompleted={drawerKind === "completed"}
        data={data}
        onClose={() => setDrawerKind(null)}
      />
    </div>
  );
}
